import { AdmPlan } from "@custom-types/base-types";

/**
 * Whether the plan can be deactivated:
 *
 * active: these plans can obviously be deactivated
 *
 * invalid: these plans (like passing the limits) are still impacting the subject,
 * so they can be deactivated to remove their impact on the subject
 *
 * inactive: these plans (like start date is in future) are not currently impacting the subject,
 * but can be deactivated if they are created by mistake
 */
export function canDeactivatePlan(plan: AdmPlan): boolean {
  return (
    plan.status.identifier === "active" ||
    plan.status.identifier === "invalid" ||
    plan.status.identifier === "inactive"
  );
}

/**
 * Whether the plan can be deleted, which is only the case once it is deactivated.
 * Deleting is irreversible, so deactivating first makes it a deliberate second step.
 */
export function canDeletePlan(plan: AdmPlan): boolean {
  return plan.status.identifier === "deactivated";
}

/** The word the user has to type to confirm deleting plans */
export const DELETE_CONFIRMATION_WORD = "delete";

/** Whether the typed text confirms the deletion, ignoring case and surrounding spaces */
export function isDeletionConfirmed(text: string): boolean {
  return text.trim().toLowerCase() === DELETE_CONFIRMATION_WORD;
}

/** Actions that can be applied to several selected plans at once */
export type BulkPlanAction = "deactivate" | "delete";

/** How each bulk action is named to the user */
export const BULK_PLAN_ACTION_TEXTS: Record<
  BulkPlanAction,
  { infinitive: string; pastParticiple: string }
> = {
  deactivate: { infinitive: "Deactivate", pastParticiple: "deactivated" },
  delete: { infinitive: "Delete", pastParticiple: "deleted" },
};

interface DeactivationCommentProps {
  /** The comment of the plan, which is kept */
  planComment: string;

  /** Why the plan is deactivated */
  reason: string;
}

/** Returns the description of a deactivated plan, which keeps its comment and adds the reason */
export function getDeactivationComment({
  planComment,
  reason,
}: DeactivationCommentProps): string {
  return `${planComment}\n\nDeactivation Reason:\n${reason}`;
}
